export const extractADWFileSelection = (selectedFiles: any, path: string[]): any => {
    if (selectedFiles === undefined || selectedFiles === null) return false;
    const key = path[0];
    const remaining = path.slice(1, path.length);
    if (remaining.length > 0) {
        return extractADWFileSelection(selectedFiles[key], remaining);
    }
    return selectedFiles[key];
}

export const isFileSelected = (selectedFiles: any, item: any): boolean => {
    // Suffix node
    if (item.selectionPath) {
        const selectedValue = extractADWFileSelection(selectedFiles ?? {}, item.selectionPath);
        return !!(selectedValue);
    }
    if (item.children) {
        // if at least one child leaf is selected
        for (const child of item.children) {
            if(child.type === 'entity') continue;
            const selectedValue = isFileSelected(selectedFiles, child);
            if (selectedValue) return true;
        }
    }
    return false;
}

export const getBidsDataTypes = (bidsFiles: any[]) => {
    const types: any = {};
    bidsFiles.forEach((file: any) => {
        if (!types[file['data_type']]) {
            types[file['data_type']] = [];
        }
        types[file['data_type']].push(file);
    });
    return types;
}

export const getBidsFiles = (bidsFiles: any[]) => {
    if (!bidsFiles?.length) return [];

    const descriptions: any = {};
    const files: any = {};
    bidsFiles
        .filter((file: any) => {
            const isDescription = !file.file_dataset && !file.modality && file.description;
            if (isDescription) {
                descriptions[file.data_type] = file.description;
            }
            return !isDescription;
        })
        .forEach((file: any) => {
            const dataset = file.file_dataset;

            if (!files[dataset]) {
                files[dataset] = [];
            }
            const datasetFiles = files[dataset];

            if (!datasetFiles[file.modality]) {
                datasetFiles[file.modality] = [];
            }
            const datasetModalityFiles = datasetFiles[file.modality];

            if (!datasetModalityFiles[file.data_type]) {
                datasetModalityFiles[file.data_type] = [];
            }
            datasetModalityFiles[file.data_type].push(file);
            if (descriptions[file.data_type]) {
                datasetModalityFiles[file.data_type].description = descriptions[file.data_type];
            }
        });

    return files;
}

export const getBidsEntities = (bidsFiles: any[], suffix: string) => {
    const entities = new Map();
    bidsFiles
        .filter((file: any) =>
            file.suffix === suffix
        )
        .forEach((file: any) =>
            Object.keys(file.entity_options || {}).forEach((key) => {
                entities.has(key) ?
                    file.entity_options[key].values.forEach((v: string) => entities.get(key).add(v.toLowerCase())) :
                    entities.set(key, new Set(file.entity_options[key].values.map((value: string) => value.toLowerCase())))
            })
        );

    return entities;
}

const getGroupTree = (parentNode: any, file: any) => {
    const groups: any[] = file.virtual_groups?.length ?
        file.virtual_groups :
        file.groups;

    let pNode = parentNode;
    groups?.forEach((group: string) => {
        if (!pNode[group]) {
            pNode[group] = [];
        }
        pNode = pNode[group]
    });

    pNode.push(file);
    return pNode;
};

export const getTabular = (tabular: any[], dataset: string) => {
    const data: any = {};
    const descriptions: any = [];

    tabular
        .filter((tabularFile: any) => {
            const isDescription = !tabularFile.file_dataset && !tabularFile.suffix && tabularFile.description;
            if (isDescription) {
                descriptions.push(tabularFile);
            }
            return !isDescription;
        })
        .filter((tabularFile) => tabularFile.file_dataset === dataset)
        .forEach((tabularFile) => {
            getGroupTree(data, tabularFile);
        });

    descriptions.map((description: any) => {
        setWithPath(data, [...description.groups, "description"], description.description);
    });
    return data;
}

export const getPipelines = (derivatives: any[]) => {
    const pipelines: any = {};
    const descriptions: any = {};

    const keyFor = (file: any) => {
        return JSON.stringify({
            dataset: file.file_dataset,
            pipeline: file.pipeline,
            groups: file.groups || [],
            suffix: file.suffix ?? null,
        });
    }

    const counts: Record<string, number> = {};

    derivatives.forEach((file: any) => {
        const isDescription = !file.file_dataset && !file.suffix && file.description;
        if (!isDescription) {
            const key = keyFor(file);
            counts[key] = (counts[key] || 0) + 1;
        }
    });

    derivatives
        .filter((file: any) => {
            const isDescription = !file.file_dataset && !file.suffix && file.description;
            if (isDescription) {
                const keys = file.pipeline ? [file.pipeline] : file.groups || [];
                setWithPath(descriptions, keys, file.description);
            }
            return !isDescription;
        })
        .forEach((derivativeFile: any) => {
            const key = keyFor(derivativeFile);
            const derivativeFileWithOptions = {
                ...derivativeFile,
                ['add_extension_to_selectionPath']:
                    counts[key] > 1 ||
                    (
                        !(derivativeFile.groups?.length) &&
                        !derivativeFile.suffix
                    ),
            };

            const dataset = derivativeFileWithOptions.file_dataset;
            if (!pipelines[dataset]) {
                pipelines[dataset] = [];
            }
            const datasetDerivatives = pipelines[dataset];

            if (!datasetDerivatives[derivativeFileWithOptions['source_type']]) {
                datasetDerivatives[derivativeFileWithOptions['source_type']] = [];
            }
            const derivativeGroup = datasetDerivatives[derivativeFileWithOptions['source_type']];

            if (!derivativeGroup[derivativeFileWithOptions['pipeline']]) {
                derivativeGroup[derivativeFileWithOptions['pipeline']] = [];
            }

            getGroupTree(derivativeGroup[derivativeFileWithOptions['pipeline']], derivativeFileWithOptions);
            if (descriptions[derivativeFileWithOptions['pipeline']]) {
                derivativeGroup[derivativeFileWithOptions['pipeline']].description = descriptions[derivativeFileWithOptions['pipeline']];
                // unset description now  thta it has been set
                delete descriptions[derivativeFileWithOptions['pipeline']];
            }

            const desc = getWithPath(descriptions, derivativeFileWithOptions.groups);
            if (desc) {
                setWithPath(derivativeGroup[derivativeFileWithOptions['pipeline']], [...derivativeFileWithOptions.groups, "description"], desc);
            }
        });
    return pipelines;
}

export const getBidsDerivatives = (derivatives: any[]) => {
    const types: any = {};

    derivatives.forEach((derivative: any) => {
        if (!types[derivative['data_type']]) {
            types[derivative['data_type']] = [];
        }
        types[derivative['data_type']].push(derivative);
    });

    return types;
}

export const mapADWSelectedFiles = (selectedFiles: any) => {
    const mappedFiles: any = {};

    Object.keys(selectedFiles || {}).forEach((dataType: string) => {
        Object.keys(selectedFiles[dataType] || {}).forEach((modality: string) => {
            mappedFiles[modality] = selectedFiles[dataType][modality];
        })
    })

    return mappedFiles;
}

export const filterOperators = [
    { value: 'AND', label: "All administered at least once (lower 'n')" },
    { value: 'OR', label: "At least one is administered (higher 'n')" }
]

export const capitalize = (val: string) => {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

export const getWithPath = (obj: any, path: []) => {
    const _path = path.slice();
    const lastKey = _path.pop();
    if (!lastKey) return;

    const lastObj = _path.reduce((obj: any, key: any) => obj[key] = obj[key] || {}, obj);
    return lastObj[lastKey];
};

export const setWithPath = (obj: any, path: string[], val: any, del: boolean = false) => {
    const _path = path.slice();
    const lastKey = _path.pop();
    if (!lastKey) return;

    const lastObj = _path.reduce(
        (obj: any, key: any) => {
            if (typeof obj[key] !== 'object') {
                obj[key] = {};
            }
            return obj[key];
        },
        obj
    );
    if (!del) {
        lastObj[lastKey] = val;
    } else {
        delete lastObj[lastKey];
    }
};

export const findLeaves = (node: any) => {
    let leaves: any[] = [];
    let children = [];
    if (Array.isArray(node)) {
        children = node;
    } else if (node && typeof node === 'object') {
        if (!node?.children?.length) return node;
        children = node.children;
    } else {
        return [];
    }

    children.forEach((child: any) => {
        leaves = [...leaves, findLeaves(child)];
    });
    return leaves.flat();
}

export const transformDataDictionaryItem = (dataItem: any) => {
    const order = Number(dataItem?.orderDisplay);
    return {
        'id': dataItem.id,
        'domain': dataItem.domain,
        'table_label': dataItem.tableLabel,
        'source': dataItem.source,
        'table_name': dataItem.tableName,
        'name': dataItem.name,
        'label': dataItem.label ?? dataItem?.additionalOptions?.label,
        'levels': dataItem.additionalOptions?.levels,
        'type_level': dataItem.typeLevel,
        'type_data': dataItem.typeData,
        'type_var': dataItem.typeVar,
        'type_field': dataItem.typeField,
        'order_display': Number.isNaN(order) ? null : order,
        'branching_logic': dataItem.branchingLogic,
        'instruction': dataItem.instruction,
        'identifier_columns': dataItem.additionalOptions?.identifier_columns,
        'order_sort': Number(dataItem?.additionalOptions?.order_sort),
        'note': dataItem.note,
        'url_table_warn_data': dataItem.additionalOptions?.urlTableWarnData,
        'url_warn_data': dataItem.additionalOptions?.urlWarnData,
        'url_table_warn_use': dataItem.additionalOptions?.urlTableWarnUse,
        'url_warn_use': dataItem.additionalOptions?.urlWarnUse,
        'study': dataItem.study,
        'sub_domain': dataItem.subDomain,
        'header': dataItem.header,
        'unit': dataItem.unit,
        'metric': dataItem.metric,
        'atlas': dataItem.atlas,
        'label_es': dataItem.labelEs,
        'levels_es': dataItem.additionalOptions?.levelsEs,
        'instruction_es': dataItem.instructionEs,
        'header_es': dataItem.headerEs,
        'note_es': dataItem.noteEs,
        'url_table': dataItem.additionalOptions?.urlTable,
        'table_redcap': dataItem.additionalOptions?.tableRedcap,
        'name_redcap': dataItem.additionalOptions?.nameRedcap,
        'name_redcap_exp': dataItem.additionalOptions?.nameRedcapExp,
        'name_deap': dataItem.additionalOptions?.nameDeap,
        'table_nda': dataItem.additionalOptions?.tableNda,
        'table_nda_5_0': dataItem.additionalOptions?.tableNda50,
        'name_nda': dataItem.additionalOptions?.nameNda,

        // Additional properties not in the column definition
        'sourceFrom': dataItem.tableName,
        'staticInstrument': dataItem.staticInstrument,
        'additional': dataItem.additionalOptions,
    }
}

export const replaceWithConfig = (string: string) => string?.replace(
    /CONFIG\[(.*)\]/,
    (matched: any, config: string) => (window.env[config as keyof Window["env"]] || "").toString()
);